/**
 * Toggle button for adding / removing a favorite on a stock vehicle.
 *
 * UI: a soft-gray pill with a heart glyph on the left and "お気に入り" label.
 * The heart is filled red / outlined gray depending on whether the vehicle
 * is currently in the customer's favorites. Optimistic UI: the heart flips
 * immediately on click and reverts on API failure.
 *
 * Favorites set is stored in Customer Portal via POST/DELETE
 * /api/portal/stock/favorites — once toggled on, the vehicle shows up in
 * the Dashboard "Your Favorites" hub section and on /stock/favorites.
 */
import { useState } from 'react';
import { portalApi, PortalApiError } from '../../lib/apiClient';

interface Props {
  vehicleId: string;
  initialFavorited: boolean;
  onChanged?: (favorited: boolean) => void;
}

export function FavoriteToggleButton({ vehicleId, initialFavorited, onChanged }: Props) {
  const [favorited, setFavorited] = useState(initialFavorited);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setBusy(true);
    setError(null);
    const next = !favorited;
    setFavorited(next);
    try {
      if (next) {
        await portalApi<{ ok: boolean; data: unknown }>('/api/portal/stock/favorites', {
          method: 'POST',
          body: { vehicleId },
        });
      } else {
        await portalApi<void>(`/api/portal/stock/favorites/${encodeURIComponent(vehicleId)}`, {
          method: 'DELETE',
        });
      }
      onChanged?.(next);
    } catch (err) {
      setFavorited(!next);
      const code = err instanceof PortalApiError ? err.code : 'UNKNOWN';
      setError(code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, width: '100%' }}>
      <button
        type="button"
        onClick={(e) => {
          // The button may sit inside a card-level <a> (favorites deep link):
          // never let the toggle click bubble into a navigation.
          e.preventDefault();
          e.stopPropagation();
          void toggle();
        }}
        disabled={busy}
        aria-pressed={favorited}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.625rem 1.25rem',
          minWidth: 0,
          width: '100%',
          whiteSpace: 'nowrap',
          background: '#f3f4f6',
          border: '1px solid #d1d5db',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#374151',
          cursor: busy ? 'wait' : 'pointer',
          transition: 'background 150ms ease, color 150ms ease',
          opacity: busy ? 0.75 : 1,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.1875rem',
            lineHeight: 1,
            color: favorited ? '#dc2626' : '#9ca3af',
            transition: 'color 150ms ease, transform 150ms ease',
            transform: favorited ? 'scale(1.05)' : 'scale(1)',
          }}
        >
          {favorited ? '♥' : '♡'}
        </span>
        <span>Favorites</span>
      </button>
      {error ? (
        <span role="alert" style={{ color: 'crimson', fontSize: '0.75rem' }}>
          {error}
        </span>
      ) : null}
    </span>
  );
}
